/**
 * Permission utility hooks and functions for role-based access control
 */

import { SelectUser } from '@server/src/types/users.types';
import { useAuth } from '@/providers/auth.provider';
import { ROLE_PERMISSIONS, Permission } from '@/constants/permissions.constants';

/**
 * Check if a user has a specific permission
 */
export function hasPermission(user: SelectUser | null | undefined, permission: Permission): boolean {
  if (!user || !user.role) {
    return false;
  }

  const roleName = user.role.name;
  const rolePermissions = user.role.permissions as Record<string, boolean>;

  // First check custom permissions stored in DB
  if (rolePermissions && permission in rolePermissions) {
    return rolePermissions[permission] === true;
  }

  // Fallback to default role permissions (case-insensitive role name lookup)
  const normalizedRoleName = roleName.charAt(0).toUpperCase() + roleName.slice(1).toLowerCase();
  const defaultPermissions = ROLE_PERMISSIONS[roleName] ?? ROLE_PERMISSIONS[normalizedRoleName];
  if (defaultPermissions) {
    return defaultPermissions[permission] === true;
  }

  return false;
}

/**
 * Check if a user has ALL of the specified permissions
 */
export function hasAllPermissions(user: SelectUser | null | undefined, permissions: Permission[]): boolean {
  return permissions.every(permission => hasPermission(user, permission));
}

/**
 * Check if a user has ANY of the specified permissions
 */
export function hasAnyPermission(user: SelectUser | null | undefined, permissions: Permission[]): boolean {
  return permissions.some(permission => hasPermission(user, permission));
}

/**
 * Hook to check if current user has a specific permission
 */
export function usePermission(permission: Permission): boolean {
  const { user } = useAuth();
  return hasPermission(user, permission);
}

/**
 * Hook to check if current user has all specified permissions
 */
export function useAllPermissions(permissions: Permission[]): boolean {
  const { user } = useAuth();
  return hasAllPermissions(user, permissions);
}

/**
 * Hook to check if current user has any of the specified permissions
 */
export function useAnyPermission(permissions: Permission[]): boolean {
  const { user } = useAuth();
  return hasAnyPermission(user, permissions);
}

/**
 * Hook to check if current user is admin
 */
export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user?.role?.name === 'Admin';
}

/**
 * Hook to get all permissions for current user
 */
export function useUserPermissions(): Record<string, boolean> {
  const { user } = useAuth();

  if (!user || !user.role) {
    return {};
  }

  const roleName = user.role.name;
  const customPermissions = (user.role.permissions as Record<string, boolean>) || {};
  const defaultPermissions = ROLE_PERMISSIONS[roleName] || {};

  // Merge custom permissions over defaults
  return { ...defaultPermissions, ...customPermissions };
}
